/**
 * validation.ts — Client-side input validation.
 *
 * Mirrors the server-side Zod schemas so the UI can give instant feedback
 * without a round-trip. Server validation is still the authoritative check.
 */

import type { VideoSource } from '../../../../shared/protocol';
import { parseMediaUrl } from '../video/mediaUrlParser';

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

export interface ValidationResult {
  valid: boolean;
  error?: string;
}

export interface VideoValidationResult extends ValidationResult {
  source?: VideoSource;
}

// ---------------------------------------------------------------------------
// Nickname
// ---------------------------------------------------------------------------

/**
 * Validate a room nickname.
 *
 * Rules:
 * - Must not be empty after trimming.
 * - Maximum 24 characters.
 * Markup-like characters are safe because nicknames are rendered as text.
 *
 * @param nickname - Raw nickname string entered by the user.
 */
export function validateNickname(nickname: string): ValidationResult {
  const trimmed = nickname.trim();

  if (!trimmed) {
    return { valid: false, error: 'Nickname is required' };
  }

  if (trimmed.length > 24) {
    return { valid: false, error: 'Nickname must be 24 characters or less' };
  }

  if (/[\x00-\x1F\x7F]/.test(trimmed)) return { valid: false, error: 'Invalid characters in nickname' };

  return { valid: true };
}

// ---------------------------------------------------------------------------
// Chat message
// ---------------------------------------------------------------------------

/**
 * Validate a chat message.
 *
 * Rules:
 * - Must not be empty after trimming.
 * - Maximum 500 characters.
 * Markup-like content is retained as plain text and escaped by React.
 *
 * @param message - Raw message string entered by the user.
 */
export function validateMessage(message: string): ValidationResult {
  const trimmed = message.trim();

  if (!trimmed) {
    return { valid: false, error: 'Message cannot be empty' };
  }

  if (trimmed.length > 500) {
    return {
      valid: false,
      error: 'Message must be 500 characters or less',
    };
  }

  return { valid: true };
}

// ---------------------------------------------------------------------------
// Video URL
// ---------------------------------------------------------------------------

/**
 * Validate a video URL or raw video ID.
 *
 * Supports known providers, direct media, HLS, and generic HTTPS embeds.
 *
 * @param url - A YouTube URL or raw 11-character video ID.
 */
export function validateVideoUrl(url: string): VideoValidationResult {
  const trimmed = (url ?? '').trim();

  if (!trimmed) {
    return { valid: false, error: 'Video URL is required' };
  }

  const source = parseMediaUrl(trimmed);

  if (!source) {
    return {
      valid: false,
      error: 'Cole uma URL HTTPS válida. Links sem provider específico serão tentados em um iframe.',
    };
  }

  return { valid: true, source };
}

// ---------------------------------------------------------------------------
// Room password
// ---------------------------------------------------------------------------

/**
 * Validate a room password (used when creating or joining a password-protected room).
 *
 * Rules:
 * - May be empty (open rooms have no password).
 * - Maximum 100 characters if provided.
 *
 * @param password - Raw password string.
 */
export function validateRoomPassword(password: string): ValidationResult {
  // Empty password is allowed (open room)
  if (!password) return { valid: true };

  if (password.length > 100) {
    return { valid: false, error: 'Password must be 100 characters or less' };
  }

  return { valid: true };
}
